import React, { useRef, useEffect } from 'react';
import {
  DocumentEditorContainerComponent,
  Toolbar,
  Inject,
} from '@syncfusion/ej2-react-documenteditor';
import { getApiBaseUrl } from '../utils/apiUrl';

// Syncfusion CSS importieren
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-react-documenteditor/styles/material.css';

// Syncfusion Lizenz registrieren
import { registerLicense } from '@syncfusion/ej2-base';

const SYNCFUSION_LICENSE_KEY = process.env.REACT_APP_SYNCFUSION_LICENSE_KEY || '';
if (SYNCFUSION_LICENSE_KEY) {
  registerLicense(SYNCFUSION_LICENSE_KEY);
}

interface SyncfusionViewerProps {
  documentId: number;
  height?: string;
}

const SyncfusionViewer: React.FC<SyncfusionViewerProps> = ({ documentId, height = '500px' }) => {
  const containerRef = useRef<DocumentEditorContainerComponent>(null);
  const [isEditorReady, setIsEditorReady] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Dokument laden wenn documentId angegeben ist
  useEffect(() => {
    const loadDocument = async () => {
      if (!isEditorReady || !containerRef.current?.documentEditor) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const apiUrl = getApiBaseUrl();
        const response = await fetch(`${apiUrl}/api/syncfusion/ImportDocument/${documentId}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const sfdt = await response.text();

        // SFDT in Editor laden
        containerRef.current.documentEditor.open(sfdt);

        // Read-only Modus aktivieren
        containerRef.current.documentEditor.isReadOnly = true;

        setLoading(false);
      } catch (err: any) {
        console.error('SyncfusionViewer: Fehler beim Laden des Dokuments:', err);
        setError(err.message || 'Fehler beim Laden des Dokuments');
        setLoading(false);
      }
    };

    loadDocument();
  }, [documentId, isEditorReady]);

  // Editor bereit-Callback
  const handleCreated = () => {
    setIsEditorReady(true);

    // Read-only Modus sofort aktivieren
    if (containerRef.current?.documentEditor) {
      containerRef.current.documentEditor.isReadOnly = true;
    }
  };

  if (error) {
    return (
      <div style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#d32f2f',
        backgroundColor: '#ffebee',
        borderRadius: '4px',
        padding: '16px'
      }}>
        Fehler: {error}
      </div>
    );
  }

  return (
    <div style={{ height, position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 10
        }}>
          Dokument wird geladen...
        </div>
      )}
      <DocumentEditorContainerComponent
        ref={containerRef}
        height="100%"
        enableToolbar={false}
        enableSpellCheck={false}
        enableComment={false}
        showPropertiesPane={false}
        restrictEditing={true}
        locale="de"
        created={handleCreated}
      >
        <Inject services={[Toolbar]} />
      </DocumentEditorContainerComponent>
    </div>
  );
};

export default SyncfusionViewer;
